/**
 * KAN-810: lead temperature (hot / warm / cool) of an open lead.
 * KAN-927: fourth level cold ("Ледяной") below cool.
 * One source of order, labels and Tailwind styles for every place that shows
 * the level: attention block, CRM board, the /leads page, the client card.
 * Class strings are written out in full so Tailwind keeps them in the build.
 */

export const LEAD_TEMPERATURE_LEVELS = ['hot', 'warm', 'cool', 'cold'] as const;

export type LeadTemperatureLevel = (typeof LEAD_TEMPERATURE_LEVELS)[number];

/** Who set the level: the bot in the dialog, a manager by hand, or the backfill run. */
export type LeadTemperatureSource = 'bot' | 'manual' | 'backfill';

export const LEAD_TEMPERATURE_LABELS: Record<LeadTemperatureLevel, string> = {
  hot: 'Горячий',
  warm: 'Тёплый',
  cool: 'Прохладный',
  cold: 'Ледяной',
};

export const LEAD_TEMPERATURE_SOURCE_LABELS: Record<LeadTemperatureSource, string> = {
  bot: 'бот',
  manual: 'вручную',
  backfill: 'разметка',
};

export interface LeadTemperatureStyle {
  /** Row tint with a left accent line. */
  row: string;
  /** Badge colors. */
  pill: string;
}

const STYLES: Record<LeadTemperatureLevel, LeadTemperatureStyle> = {
  hot: { row: 'bg-red-50 border-l-4 border-l-red-500', pill: 'bg-red-100 text-red-700' },
  warm: { row: 'bg-amber-50 border-l-4 border-l-amber-500', pill: 'bg-amber-100 text-amber-800' },
  cool: { row: 'bg-sky-50 border-l-4 border-l-sky-500', pill: 'bg-sky-100 text-sky-700' },
  cold: { row: 'bg-slate-50 border-l-4 border-l-slate-500', pill: 'bg-slate-100 text-slate-700' },
};

/**
 * Accent line for the first cell of a table row: in a `border-separate` table
 * the border of a <tr> is not drawn, so the line goes on the first <td>.
 */
const CELL_ACCENTS: Record<LeadTemperatureLevel, string> = {
  hot: 'border-l-4 border-l-red-500',
  warm: 'border-l-4 border-l-amber-500',
  cool: 'border-l-4 border-l-sky-500',
  cold: 'border-l-4 border-l-slate-500',
};

export function isLeadTemperatureLevel(value: unknown): value is LeadTemperatureLevel {
  return typeof value === 'string' && (LEAD_TEMPERATURE_LEVELS as readonly string[]).includes(value);
}

export function isLeadTemperatureSource(value: unknown): value is LeadTemperatureSource {
  return typeof value === 'string' && value in LEAD_TEMPERATURE_SOURCE_LABELS;
}

/** Row and badge classes of a level; null for an empty or unknown level. */
export function leadTemperatureStyle(level: string | null | undefined): LeadTemperatureStyle | null {
  return isLeadTemperatureLevel(level) ? STYLES[level] : null;
}

/** Left accent line for the first cell of a table row; "" without a level. */
export function leadTemperatureCellAccent(level: string | null | undefined): string {
  return isLeadTemperatureLevel(level) ? CELL_ACCENTS[level] : '';
}

/**
 * KAN-819: tooltip of the badge: "<level>, <source>: <reason>"; the source and
 * the reason are skipped when unknown or empty; "" for an empty level.
 */
export function leadTemperatureTooltip(
  level: string | null | undefined,
  source: string | null | undefined,
  reason: string | null | undefined
): string {
  if (!isLeadTemperatureLevel(level)) return '';
  let text: string = LEAD_TEMPERATURE_LABELS[level];
  if (isLeadTemperatureSource(source)) text += `, ${LEAD_TEMPERATURE_SOURCE_LABELS[source]}`;
  const why = (reason ?? '').trim();
  return why ? `${text}: ${why}` : text;
}

/**
 * KAN-927: "Всего" of the /leads page: the backend total, or the sum of the
 * per-level counts from an older backend without it.
 */
export function leadTemperatureTotal(
  counts: Partial<Record<LeadTemperatureLevel, number>> | undefined,
  total?: number | null
): number {
  if (typeof total === 'number') return total;
  return LEAD_TEMPERATURE_LEVELS.reduce((sum, level) => sum + (counts?.[level] ?? 0), 0);
}

/** KAN-851: chip filter of a list by level: every lead or one level. */
export type LeadTemperatureFilter = 'all' | LeadTemperatureLevel;

export const LEAD_TEMPERATURE_FILTERS: readonly LeadTemperatureFilter[] = ['all', ...LEAD_TEMPERATURE_LEVELS];

/** KAN-851: chip labels of the filter (plural, as the owner named them). */
export const LEAD_TEMPERATURE_FILTER_LABELS: Record<LeadTemperatureFilter, string> = {
  all: 'Все',
  hot: 'Горячие',
  warm: 'Тёплые',
  cool: 'Прохладные',
  cold: 'Ледяные',
};

/** KAN-851: items of the chosen level ("all" keeps every item, the order stays). */
export function filterByLeadTemperature<T>(
  items: readonly T[],
  filter: LeadTemperatureFilter,
  levelOf: (item: T) => string | null | undefined
): T[] {
  if (filter === 'all') return [...items];
  return items.filter((item) => levelOf(item) === filter);
}
